/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import * as Sentry from '@sentry/react';

import { customPersister, queryClient } from 'utils/queryClient';

import {
  setupGlobalErrorHandlers,
  trackPageLoad,
  trackMemoryUsage,
} from './utils/errorTracking';
/* eslint-disable import/order */
import {
  setupNetworkErrorTracking,
  setupFetchErrorTracking,
} from './utils/networkErrorTracking';

// Initialize Sentry with comprehensive error tracking
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE || 'development',

  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration({
      // Capture all navigation and interactions
      enableInp: true,
      enableLongTask: true,
    }),

    // Session replay for debugging
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true,
    }),

    // Breadcrumbs for user actions and network requests
    Sentry.breadcrumbsIntegration({
      // Log console errors
      console: true,
      // DOM interactions (clicks, inputs)
      dom: true,
      // Network requests
      fetch: true,
      // Navigation changes
      history: true,
      // Sentry SDK events
      sentry: true,
      // XMLHttpRequest calls
      xhr: true,
    }),

    // HTTP context for network errors
    Sentry.httpContextIntegration(),

    // Context lines around errors
    Sentry.contextLinesIntegration(),

    // Enhanced dedupe to prevent spam
    Sentry.dedupeIntegration(),
  ],

  // Performance Monitoring
  tracesSampleRate: 1.0,

  // Session Replay
  replaysSessionSampleRate: 1.0,
  // Always record when errors occur
  replaysOnErrorSampleRate: 1.0,

  // Enhanced error capture
  beforeSend(event, hint) {
    // Add extra context for all errors
    if (event.exception) {
      const error = hint.originalException;

      // Add browser and system info
      event.extra = {
        ...event.extra,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
        },
        connection: (navigator as any).connection
          ? {
              effectiveType: (navigator as any).connection.effectiveType,
              downlink: (navigator as any).connection.downlink,
            }
          : null,
      };

      // Add error-specific context
      if (error instanceof Error) {
        event.extra.errorName = error.name;
        event.extra.errorMessage = error.message;
      }
    }

    return event;
  },

  // Enhanced breadcrumb filtering
  beforeBreadcrumb(breadcrumb) {
    // Capture all clicks with more detail
    if (breadcrumb.category === 'ui.click') {
      const target = breadcrumb.data?.target;

      if (target) {
        breadcrumb.data = {
          ...breadcrumb.data,
          tagName: target.tagName,
          className: target.className,
          id: target.id,
          textContent: target.textContent?.substring(0, 100),
        };
      }
    }

    // Capture network errors with more detail
    if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      if (breadcrumb.data?.status_code && breadcrumb.data.status_code >= 400) {
        breadcrumb.level = 'error';
      }
    }

    return breadcrumb;
  },

  // Track custom tags
  initialScope: {
    tags: {
      component: 'react-app',
      framework: 'vite',
    },
  },

  // Enhanced transport options
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

  debug: import.meta.env.MODE === 'development',

  // Error sampling
  sampleRate: 1.0,

  // Additional options
  attachStacktrace: true,
  sendDefaultPii: false,

  release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
});

// Setup comprehensive error tracking
setupGlobalErrorHandlers();
setupNetworkErrorTracking();
setupFetchErrorTracking();

// Setup performance monitoring
window.addEventListener('load', () => {
  trackPageLoad();

  // Monitor memory usage every 30 seconds
  setInterval(trackMemoryUsage, 30000);
});

// Make Sentry globally available for the network tracking
declare global {
  interface Window {
    Sentry: typeof Sentry;
  }
}
window.Sentry = Sentry;

import App from './App';

import './index.css';

import './localization/i18n';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      // @ts-ignore
      persistOptions={{ persister: customPersister }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>
);
